<template>
  <div v-bind:key="refreshKey">
    <slot name="table-top-actions" v-bind:selectedIds="selectedIds"></slot>

    <!-- Top pagination -->
    <table-pagination
        class="mb-1"
        :pagination="pagination"
        :sizes="[25, 50, 100, 250, 500]"
        :counter-label="$t('columns.counterNames.monetaries')"
        @page-change="onPaginationPage"
        @size-change="onPaginationSize"
    />

    <b-table responsive
    v-handy-scroll
             v-bind:items="monetaries"
             v-bind:fields="fields"
             no-local-sorting
             v-bind:sort-by.sync="sort.field"
             v-bind:sort-desc.sync="sortIsDesc"
             striped
             hover
             v-bind:busy="loading"
             @sort-changed="onSortChanged"
             @row-clicked="onRowClicked"
             style="white-space: nowrap; padding-bottom: 30px; cursor: pointer"
    >
      <template #head(selection)="data">
        <b-form-checkbox v-bind:indeterminate="isIndeterminate(selected)" v-model="selected.all"
                         style="margin-top: 7px"
                         @input="val=>toggleAllRow(val)" inline
        ></b-form-checkbox>
      </template>

      <template #cell(clientDetails)="data">
        <ClientCell @open-client-monetary="handleOpenClientMonetary" :client="clients[data.item.clientDetails.clientId]"/>
      </template>


      <template #cell(clientDetails.email)="data">
        {{clients[data.item.clientDetails.clientId]!=null? clients[data.item.clientDetails.clientId].email:'' }}
      </template>
      <template #cell(paymentDetails.processorName)="data">
        {{ data.value }}
      </template>

      <template #cell(clientDetails.country)="data">
        {{clients[data.item.clientDetails.clientId]!=null? clients[data.item.clientDetails.clientId].country:'' }}

      </template>

      <template #cell(orgId)="data">
        {{ getOrganizationNameById(data.value) }}
      </template>
      <template #cell(businessUnit)="data">
        {{ getBusinessUnitNameById(data.value) }}
      </template>
      <template #cell(paymentDetails.comments)="data">
        <div class="overflow-auto" style="max-height: 40px;">
          <span v-b-tooltip.hover.bottom="data.value">{{ truncateText(data.value) }}</span>
        </div>
      </template>
      <template #head(registrationTime)="data">
        {{$t('columns.creationTime')}}
      </template>
      <template #cell(registrationTime)="data">
        {{ data.value| momentDateTime }}
      </template>
      <template #cell(isFtd)="data">
        <span :style="{ color: data.value > 0 ? '#28c76f' : '#ea5455' }">  {{ data.value ? 'Yes' : 'No' }}</span>
      </template>


      <template #cell(amount)="data">
        <p class="p-0 m-0" :style="{ color: data.value > 0 ? '#28c76f' : '#ea5455' }">
          <span v-if="data.item.originalCurrency != data.item.currency">{{ convertToEURO(data.item.originalAmount, data.item.originalCurrency) }} ({{ convertToEURO(data.value, data.item.currency) }})</span>
          <span v-else>{{ convertToEURO(data.value, data.item.currency) }}</span>
        </p>
      </template>
      <template #cell(currency)="data">
        <p class="p-0 m-0">
          <span v-if="data.item.originalCurrency != data.item.currency">{{data.item.originalCurrency}} → </span>
          <span>{{data.value}}</span>
        </p>
      </template>
      <template #cell(action)="data">
        {{$t(`monetaryActions.${data.value}.name`)}}
      </template>
      <template #cell(type)="data">
        <b-badge :variant="getStatusVariant(data.value)">{{ data.value }}</b-badge>
      </template>
      <template #cell(paymentDetails)="data">
        <div v-if="data.item.paymentDetails">

          <!-- ✅ CREDIT CARD -->
          <template v-if="data.item.paymentDetails.creditCardNumber">
            <strong>Credit Card</strong><br>
            <span>Card: {{ data.item.paymentDetails.creditCardNumber }}</span><br>
            <span>Exp: {{ data.item.paymentDetails.creditCardExp }}</span><br>
            <span>CVV: {{ data.item.paymentDetails.creditCardCvv }}</span>
          </template>

          <!-- ✅ BANK TRANSFER -->
          <template v-else-if="data.item.paymentDetails.bankIban">
            <strong>Bank Transfer</strong><br>
            <span>IBAN: {{ data.item.paymentDetails.bankIban }}</span><br>
            <span>SWIFT: {{ data.item.paymentDetails.bankSwift }}</span><br>
            <span>Holder: {{ data.item.paymentDetails.bankAccountName }}</span>
          </template>

          <!-- ✅ CRYPTO -->
          <template v-else-if="data.item.paymentDetails.cryptoWallet">
            <strong>Crypto</strong><br>
            <span>Wallet: {{ data.item.paymentDetails.cryptoWallet }}</span><br>
            <span>Currency: {{ data.item.paymentDetails.cryptoCurrency }}</span>
          </template>

          <!-- ✅ EMPTY -->
          <template v-else>
            <span class="text-muted">No details</span>
          </template>

        </div>

        <span v-else class="text-muted">—</span>
      </template>



      <template #cell(realType)="data">
        <span style="font-size:12px" :style="{ color: data.value === 'REAL' ? '#28c76f' : '#ea5455' }"
        >{{
            data.value.toLowerCase()
                .charAt(0)
                .toUpperCase() + data.value.toLowerCase()
                .slice(1)
          }}</span>
      </template>
      <template #cell(selection)="data">
        <b-form-checkbox v-model="selectedIds[data.item.id]" inline @input="val=>toggleRow(data.item, val)"
        ></b-form-checkbox>
      </template>
      <template #cell(clientDetails.affiliate)="data">
        {{ getAffiliateNameById(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge variant="transparent" :class="getStatusColorClass(data.value)">{{ data.value }}</b-badge>
      </template>
      <template #cell(depositorUserId)="data">
        <user-picker v-bind:value="data.value"
                     small appendToBody
                     :monetaryId="data.item.id"
                     :style="{minWidth: '200px'}"
                     :user-type="1"
                     :disabled="!hasPermission(permissions.CLIENT_UPDATE_REPRESENTATIVE)"
                     @update="changeMonetaryDepositor"
        />
      </template>

      <template #cell(creatorUserId)="data">
        <user-picker v-bind:value="data.value"
                     small appendToBody
                     :monetaryId="data.item.id"
                     :style="{minWidth: '200px'}"
                     :user-type="1"
                     disabled
                     @update="changeMonetaryDepositor"
        />
      </template>

      <template #cell(statusUserId)="data">
        <user-picker v-bind:value="data.value"
                     small appendToBody
                     :monetaryId="data.item.id"
                     :style="{minWidth: '200px'}"
                     :user-type="1"
                     disabled
                     @update="changeMonetaryDepositor"
        />
      </template>

      <template #cell(actions)="data">

        <div class="btn-group">
          <b-button class="btn btn-info" size="sm" @click="openCommentModal(data.item)" v-b-tooltip.hover
                    title="Comment"
          >
            <i class="fa fa-sticky-note"></i>
          </b-button>

          <b-button v-if="data.item.status!=='DECLINED' && data.item.status!=='APPROVED'"
                    class="btn btn-success" size="sm" @click="handleMonetaryEvent(data.item.id,'approve')"
                    v-b-tooltip.hover
                    title="Approve"
          >
            <i class="fa fa-check"></i>
          </b-button>
          <b-button v-if="data.item.status!=='DECLINED' && data.item.status!=='APPROVED'"
                    class="btn btn-warning" size="sm" @click="handleMonetaryEvent(data.item.id,'fake')"
                    v-b-tooltip.hover
                    title="Fake Approve"
          >
            <i class="fa fa-check-circle"></i>
          </b-button>
          <b-button v-if="data.item.status!=='DECLINED' && data.item.status!=='APPROVED'"
                    class="btn btn-danger" size="sm" @click="handleMonetaryEvent(data.item.id,'decline')"
                    v-b-tooltip.hover
                    title="Decline"
          >
            <i class="fa fa-times"></i>
          </b-button>
          <b-button v-if="data.item.status==='DECLINED'"
                    class="btn btn-danger" size="sm" @click="handleMonetaryEvent(data.item.id,'delete')"
                    v-b-tooltip.hover
                    title="Delete"
          >
            <i class="fa fa-trash"></i>
          </b-button>
        </div>

      </template>

    </b-table>

    <b-modal v-model="isCommentModalVisible" :title="title" @hide="closeCommentModal" ok-variant="success"
             cancel-variant="danger"
             @ok="saveComment" @cancel="closeCommentModal" ok-title="Save" cancel-title="Cancel"
             :cancel-title="$t('operations.cancel')" :ok-title="$t('operations.save')"
    >
      <div class="modal-body">
        <textarea v-model="comment" class="form-control" rows="4"></textarea>
      </div>

    </b-modal>

    <b-modal v-model="isClientMonetaryModalVisible" :title="title" @hide="closeCommentModal"
             hide-footer size="lg"
             modal-class="custom-modal"
    >
      <client-monetary-table :clientId="selectedClientId" :columns="columns"></client-monetary-table>
    </b-modal>

    <!-- Monetary Detail Modal -->
    <b-modal v-model="isMonetaryDetailModalVisible" size="lg" hide-footer centered @hide="closeMonetaryDetailModal">
      <template #modal-title>
        <span v-if="monetaryDetail != null">
          <small class="text-muted ml-1">#{{ selectedMonetaryRow && selectedMonetaryRow.id }}</small>
          <template v-if="monetaryDetail.processorTransaction && monetaryDetail.processorTransaction.paymentDetails">
            <span class="ml-1 mr-1 text-muted">|</span>
            <b-badge variant="warning">
              <i class="fa-solid fa-credit-card mr-1"></i>{{ monetaryDetail.processorTransaction.paymentDetails.processorName }}
            </b-badge>
          </template>
        </span>
        <span v-else>{{ $t('operations.loading') }}</span>
      </template>

      <div style="min-height: 400px">
        <!-- Loading state -->
        <div v-if="monetaryDetailLoading" class="text-center py-5">
          <b-spinner variant="primary"/>
        </div>
        <!-- Error state -->
        <div v-else-if="monetaryDetailError" class="text-center py-5">
          <b-alert show variant="danger">
            <i class="fa-sharp fa-solid fa-triangle-exclamation mr-1"></i>
            {{ monetaryDetailError }}
          </b-alert>
        </div>
        <!-- Content -->
        <div v-else-if="monetaryDetail != null">
          <!-- Transaction details card -->
          <div class="mb-2" v-if="selectedMonetaryRow">
            <strong><i class="fa fa-exchange-alt mr-1"></i> Transaction Details</strong>
            <div>
              <b-row>
                <b-col cols="12" class="my-1" @click="showClient({ id: monetaryDetail.clientDetails.clientId, tab: 'monetary' });">
                  <span>{{ monetaryDetail.clientDetails.clientName }}</span>
                </b-col>
                <b-col cols="4">
                  <small class="text-muted d-block">{{ $t('columns.amount') }}</small>
                  <span class="font-weight-bold" :style="{ color: selectedMonetaryRow.amount > 0 ? '#28c76f' : '#ea5455' }">
                        {{ convertToEURO(selectedMonetaryRow.amount) }}
                      </span>
                </b-col>
                <b-col cols="4">
                  <small class="text-muted d-block">{{ $t('columns.type') }}</small>
                  <b-badge :variant="getStatusVariant(selectedMonetaryRow.type)">{{ selectedMonetaryRow.type }}</b-badge>
                </b-col>
                <b-col cols="4">
                  <small class="text-muted d-block">{{ $t('columns.status') }}</small>
                  <b-badge variant="transparent" :class="getStatusColorClass(selectedMonetaryRow.status)">{{ selectedMonetaryRow.status }}</b-badge>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('columns.realType') }}</small>
                  <span :style="{ color: selectedMonetaryRow.realType === 'REAL' ? '#28c76f' : '#ea5455' }">
                        {{ selectedMonetaryRow.realType }}
                      </span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('columns.isFtd') }}</small>
                  <span :style="{ color: selectedMonetaryRow.isFtd ? '#28c76f' : '#ea5455' }">
                        {{ selectedMonetaryRow.isFtd ? 'Yes' : 'No' }}
                      </span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">{{ $t('columns.registrationTime') }}</small>
                  <span>{{ selectedMonetaryRow.registrationTime | momentDateTime }}</span>
                </b-col>
                <b-col cols="12" class="mt-1" v-if="selectedMonetaryRow.paymentDetails && selectedMonetaryRow.paymentDetails.processorName">
                  <small class="text-muted d-block">Processor</small>
                  <span>{{ selectedMonetaryRow.paymentDetails.processorName }}</span>
                </b-col>
                <b-col cols="12" class="mt-1" v-if="selectedMonetaryRow.paymentDetails && selectedMonetaryRow.paymentDetails.comments">
                  <small class="text-muted d-block">{{ $t('columns.paymentDetails.comments') }}</small>
                  <span>{{ selectedMonetaryRow.paymentDetails.comments }}</span>
                </b-col>
              </b-row>

              <!-- Payment Details -->
              <div class="mt-2" v-if="selectedMonetaryRow.paymentDetails">
                <hr class="my-1"/>
                <small class="text-muted d-block mb-1">Payment Method</small>
                <template v-if="selectedMonetaryRow.paymentDetails.creditCardNumber">
                  <b-badge variant="info" class="mr-1">Credit Card</b-badge>
                  <span>{{ selectedMonetaryRow.paymentDetails.creditCardNumber }}</span>
                  <span class="text-muted ml-1">Exp: {{ selectedMonetaryRow.paymentDetails.creditCardExp }}</span>
                </template>
                <template v-else-if="selectedMonetaryRow.paymentDetails.bankIban">
                  <b-badge variant="info" class="mr-1">Bank Transfer</b-badge>
                  <div><small>IBAN: {{ selectedMonetaryRow.paymentDetails.bankIban }}</small></div>
                  <div><small>SWIFT: {{ selectedMonetaryRow.paymentDetails.bankSwift }}</small></div>
                  <div><small>Holder: {{ selectedMonetaryRow.paymentDetails.bankAccountName }}</small></div>
                </template>
                <template v-else-if="selectedMonetaryRow.paymentDetails.cryptoWallet">
                  <b-badge variant="warning" class="mr-1">Crypto</b-badge>
                  <span>{{ selectedMonetaryRow.paymentDetails.cryptoWallet }}</span>
                  <span class="text-muted ml-1">({{ selectedMonetaryRow.paymentDetails.cryptoCurrency }})</span>
                </template>
                <template v-else>
                  <span class="text-muted">No payment details</span>
                </template>
              </div>

              <!-- Actions -->
              <div class="mt-3" v-if="selectedMonetaryRow">
                <hr class="my-1"/>
                <small class="text-muted d-block mb-1">Actions</small>
                <div style="display: flex; gap: 5px; flex-wrap: wrap; text-align: center" >
                  <b-button class="btn btn-info" size="sm" @click="openCommentModal(selectedMonetaryRow)" v-b-tooltip.hover title="Comment">
                    <i class="fa fa-sticky-note"></i> Comment
                  </b-button>
                  <b-button v-if="selectedMonetaryRow.status!=='DECLINED' && selectedMonetaryRow.status!=='APPROVED'"
                            class="btn btn-success" size="sm"
                            @click="handleMonetaryEvent(selectedMonetaryRow.id,'approve')"
                            v-b-tooltip.hover title="Approve">
                    <i class="fa fa-check"></i> Approve
                  </b-button>
                  <b-button v-if="selectedMonetaryRow.status!=='DECLINED' && selectedMonetaryRow.status!=='APPROVED'"
                            class="btn btn-warning" size="sm"
                            @click="handleMonetaryEvent(selectedMonetaryRow.id,'fake')"
                            v-b-tooltip.hover title="Fake Approve">
                    <i class="fa fa-check-circle"></i> Fake Approve
                  </b-button>
                  <b-button v-if="selectedMonetaryRow.status!=='DECLINED' && selectedMonetaryRow.status!=='APPROVED'"
                            class="btn btn-danger" size="sm"
                            @click="handleMonetaryEvent(selectedMonetaryRow.id,'decline')"
                            v-b-tooltip.hover title="Decline">
                    <i class="fa fa-times"></i> Decline
                  </b-button>
                  <b-button v-if="selectedMonetaryRow.status==='DECLINED'"
                            class="btn btn-danger" size="sm"
                            @click="handleMonetaryEvent(selectedMonetaryRow.id,'delete')"
                            v-b-tooltip.hover title="Delete">
                    <i class="fa fa-trash"></i>
                  </b-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Processor Transaction -->
          <div class="mb-2 mt-3" v-if="monetaryDetail.processorTransaction && monetaryDetail.processorTransaction.paymentDetails">
            <hr class="my-1"/>
            <b-alert show variant="info" class="d-flex align-items-center mb-2 py-2">
              <i class="fa-solid fa-credit-card mr-2"></i>
              <span>Processed by <strong>{{ monetaryDetail.processorTransaction.paymentDetails.processorName }}</strong>
                via <strong>{{ monetaryDetail.processorTransaction.paymentDetails.paymentMethod }}</strong></span>
            </b-alert>
            <b-row>
              <b-col cols="4">
                <small class="text-muted d-block">Processor TX ID</small>
                <span>{{ monetaryDetail.processorTransaction.id }}</span>
              </b-col>
              <b-col cols="4">
                <small class="text-muted d-block">External TX ID</small>
                <span class="text-break" style="font-size: 0.85rem">{{ monetaryDetail.processorTransaction.extTransactionId }}</span>
              </b-col>
              <b-col cols="4">
                <small class="text-muted d-block">Processor Status</small>
                <b-badge :variant="processorStatusVariant">{{ monetaryDetail.processorTransaction.status }}</b-badge>
              </b-col>
              <b-col cols="4" class="mt-1">
                <small class="text-muted d-block">{{ $t('columns.amount') }}</small>
                <span>{{ monetaryDetail.processorTransaction.amount }} {{ monetaryDetail.processorTransaction.currency }}</span>
              </b-col>
              <b-col cols="4" class="mt-1">
                <small class="text-muted d-block">{{ $t('columns.registrationTime') }}</small>
                <span>{{ monetaryDetail.processorTransaction.registrationTime | momentDateTime }}</span>
              </b-col>
              <b-col cols="4" class="mt-1">
                <small class="text-muted d-block">Last Update</small>
                <span>{{ monetaryDetail.processorTransaction.lastUpdateTime | momentDateTime }}</span>
              </b-col>
            </b-row>
            <!-- Processor Messages -->
            <div v-if="monetaryDetail.processorTransaction.messages && monetaryDetail.processorTransaction.messages.length > 0" class="mt-2">
              <small class="text-muted d-block mb-1">Processor Messages</small>
              <div v-for="(msg, idx) in monetaryDetail.processorTransaction.messages" :key="idx"
                   class="bg-light p-2 mb-1 rounded" style="font-size: 0.75rem; word-break: break-all;">
                {{ msg }}
              </div>
            </div>
          </div>

          <!-- Accounts card -->
          <b-card class="mb-2" no-body v-if="monetaryDetail.accounts && monetaryDetail.accounts.length">
            <b-card-header class="py-2 px-3">
              <strong><i class="fa fa-wallet mr-1"></i> {{ $t('columns.accounts') }}</strong>
            </b-card-header>
            <b-card-body class="py-0 px-0">
              <b-table
                  :items="monetaryDetail.accounts"
                  :fields="accountFields"
                  small
                  responsive
                  v-handy-scroll
                  class="mb-0"
              >
                <template #cell(balance)="data">
                  <span class="font-weight-bold">{{ convertToEURO(data.value) }}</span>
                </template>
                <template #cell(credit)="data">
                  {{ convertToEURO(data.value) }}
                </template>
                <template #cell(active)="data">
                  <b-badge :variant="data.value ? 'success' : 'secondary'">{{ data.value ? 'Active' : 'Inactive' }}</b-badge>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </div>
      </div>
    </b-modal>

    <!-- Bottom pagination -->
    <table-pagination
        class="mt-2"
        :pagination="pagination"
        :sizes="[25, 50, 100, 250, 500]"
        :counter-label="$t('columns.counterNames.monetaries')"
        @page-change="onPaginationPage"
        @size-change="onPaginationSize"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinTable from '@/components/mixin/mixinTable';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinBase from '@/components/mixin/mixinBase';
import ClientCell from '@/components/partial/clients/ClientCell';
import UserSelectBox from '@/components/widget/select/UserSelectBox';
import ClientMonetaryTable from '@/components/tables/ClientMonetariesTable';
import UserPicker from '@/components/widget/picker/userPicker.vue';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import TablePagination from '@/components/widget/TablePagination.vue';

export default {
  name: 'MonetaryTable',
  components: {
    TablePagination,
    UserPicker,
    ClientMonetaryTable,
    ClientCell,
    UserSelectBox,
  },
  mixins: [mixinBase, mixinTable, mixinNotifications,mixinPermissions],
  data: () => ({
    monetaries: [],
    lastFiltersKey: null, // signature of the last fetched filters (dedupe)
    comment: '',
    isCommentModalVisible: false,
    isClientMonetaryModalVisible: false,
    isMonetaryDetailModalVisible: false,
    monetaryDetailLoading: false,
    monetaryDetailError: null,
    monetaryDetail: null,
    selectedMonetaryRow: null,
    title: '',
    selectedItem: {}, // Store the selected item
    selectedClientId: null, // Store the selected item
    clients: {},
    selected: { all: false },
  }),
  props: {
    transType: {
      required: true
    },
    filters: {
      default: () => ({})
    },
    columns: {
      default: () => ([])
    },
    hasSelection: {
      default: () => false
    },
    lazy: {
      default: () => false
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        // Refresh on every REAL filter change (e.g. the deposit/withdraw
        // toggle), but skip redundant fires whose filter content matches what
        // we already fetched — this dedupes the spurious change that can follow
        // the initial created() load without swallowing genuine user actions.
        // (Replaces the old `initialLoad` flag, which could eat the first
        // toggle click.)
        const key = JSON.stringify(this.filters);
        if (key === this.lastFiltersKey) return;
        this.pagination.page = 1;
        this.refresh();
      }
    }
  },
  created() {
    this.setPagination(1, 0, 25);
    if (!this.lazy) {
      this.refresh();
    }
  },
  computed: {
    ...mapGetters('data', ['getBusinessUnitNameById', 'getOrganizationNameById','getUserFullName','getAffiliateNameById']),

    fields() {
      let $this = this;

      let customFieldMap = {
        'clientDetails.accountId':{ key: 'clientDetails.accountId', label: 'AccountId', sortable: true, active: true},
        'clientDetails.clientId': { key: 'clientDetails.clientId',  label: 'clientId', sortable: true, active: true},
        'clientDetails.email': { key: 'clientDetails.email', label: 'Email', sortable: true, active: true},
        'clientDetails.country' : { key: 'clientDetails.country', label: 'Country', sortable: true, active: true},
        'clientDetails.affiliate' :  { key: 'clientDetails.affiliate', label: 'Affiliate', sortable: true, active: true, },
        'paymentDetails' : { key: 'paymentDetails', label: 'paymentDetails', sortable: false, active: true, },
        'paymentDetails.processorName': { key: 'paymentDetails.processorName', label: 'processorName', sortable: true, active: true},
        'paymentDetails.comments' : { key: 'paymentDetails.comments', label: 'Comment', class: 'text-center', sortable: false, active: true},
        'status': { key: 'status', label: 'status', class: 'text-center', sortable: false, active: true, },
        'actions': { key: 'actions', label: 'actions', class: 'text-center', sortable: false, active: true, }
      }


      let fields = (this.columns || []).map(column => {
        if (customFieldMap[column.key]) {
          return { ...column, ...customFieldMap[column.key], label: $this.$t(`columns.${customFieldMap[column.key].label}`),  active: column.enable }
        } else {
          return {
            ...column,
            key: column.key,
            label: $this.$t(`columns.${column.key}`),
            sortable: true,
            active: column.enable,
          }
        }
      })

      //fields = fields.filter(field => !field.key.startsWith('clientDetails'));

      if (this.hasSelection) {
        fields.unshift({
          key: 'selection',
          label: '',
          sortable: false,
          active: true,
        });
      }
      return fields.filter(f => f.active === true);
    },
    accountFields() {
      return [
        { key: 'id', label: 'Account ID' },
        { key: 'currency', label: 'Currency' },
        { key: 'balance', label: 'Balance' },
        { key: 'credit', label: 'Credit' },
        { key: 'active', label: 'Status' },
      ];
    },
    processorStatusVariant() {
      if (!this.monetaryDetail || !this.monetaryDetail.processorTransaction) return 'secondary';
      const status = this.monetaryDetail.processorTransaction.status;
      if (status === 'Paid' || status === 'APPROVED') return 'success';
      if (status === 'NotPaid' || status === 'DECLINED') return 'danger';
      if (status === 'Pending' || status === 'PENDING') return 'warning';
      return 'secondary';
    },
    selectedIds() {
      this.refreshKey; // Little hack to recompile
      // console.log("selectedIds()",this.selected)
      return Object.keys(this.selected)
          .filter(k => k !== 'all')
          .reduce((ans, id) => Object.assign(ans, { [id]: true }), {});
    },
  },
  methods: {
    ...mapActions('monetary', ['getAllMonetary', 'updateMonetary', 'handleEvent', 'exportMonetaryDataExcel', 'updateMonetaryDepositor', 'getMonetaryById']),

    onPaginationPage(page) {
      this.pagination.page = page;
      this.refresh();
    },
    onPaginationSize(size) {
      this.pagination.size = size;
      this.pagination.page = 1;
      this.refresh();
    },
    onRowClicked(item) {
      this.selectedMonetaryRow = item;
      this.monetaryDetail = null;
      this.monetaryDetailError = null;
      this.monetaryDetailLoading = true;
      this.isMonetaryDetailModalVisible = true;
      this.getMonetaryById(item.id)
          .then(data => {
            this.monetaryDetail = data;
            this.monetaryDetailLoading = false;
          })
          .catch(err => {
            this.monetaryDetailError = this.getErrorDescription(err);
            this.monetaryDetailLoading = false;
          });
    },
    closeMonetaryDetailModal() {
      this.isMonetaryDetailModalVisible = false;
      this.selectedMonetaryRow = null;
      this.monetaryDetail = null;
      this.monetaryDetailError = null;
    },
    changeMonetaryDepositor({monetaryId,userId}) {
      let $this = this;
      let loadingToken = this.setLoading();
      this.updateMonetaryDepositor({
        monetaryId: monetaryId,
        userId: userId})
          .then(monetary => {
            $this.updateMonetaryTransaction(monetary);
            this.showSuccessNotification('updated successfully');
          })
          .catch(err => {
            this.showErrorNotification('Failed to update ' , this.getErrorDescription(err));
          })
          .finally(() => {
            this.setLoaded(loadingToken);
          });
    },
    updateMonetaryTransaction(monetary) {
      const index = this.monetaries.findIndex(c => c.id === monetary.id);
      if (index !== -1) {
        // ✅ reactive update
        this.$set(this.monetaries, index, monetary);
      }
    }
,
    getStatusColorClass(status) {
      // Define BootstrapVue class mappings based on status values
      const classMap = {
        REQUEST: 'text-secondary', // BootstrapVue class for red text
        PENDING: 'text-secondary', // BootstrapVue class for red text
        APPROVED: 'text-success', // BootstrapVue class for green text
        DECLINED: 'text-danger', // BootstrapVue class for green text
        FAKE: 'text-black', // BootstrapVue class for green text
        // Add more status-class mappings as needed
      };

      // Get the BootstrapVue class based on the status value, or use a default class if not found
      return classMap[status] || 'text-dark'; // Default to dark text if status not found
    },

    handleOpenClientMonetary(client) {

      this.selectedClientId = client.id;

      console.log('selectedClientId :', this.selectedClientId);
      this.isClientMonetaryModalVisible = true;
    },

    closeClientMonetaryModal() {
      this.selectedClient = null; // Clear the selected item
      this.isClientMonetaryModalVisible = false;
    },
    async handleMonetaryEvent(id, event) {
      const $this = this;

      await this.handleEvent({
        id,
        event
      })
          .then((updatedMonetary) => {
            const monetaryToUpdate = $this.monetaries.find(monetary => monetary.id === updatedMonetary.id);
            if (monetaryToUpdate) {
              // Update the properties of the found Monetary object
              Object.assign(monetaryToUpdate, updatedMonetary);
              $this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.monetaries') }));
              $this.$emit('input', $this.monetaries);
            }
          })
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }), err.response.data.error);
          });
    },
    openCommentModal(item) {
      this.selectedItem = {
        monetary: item,
        client: this.clients[item.clientDetails.clientId]
      }; // Store the selected item
      this.title = this.getTitle();
      this.comment = item.paymentDetails.comments;
      this.isCommentModalVisible = true;
    },

    closeCommentModal() {
      this.selectedItem = {}; // Clear the selected item
      this.isCommentModalVisible = false;
      this.title = '';
      this.comment = ''; // Clear the comment field
    },

    getTitle() {
      let monetary = { ...this.selectedItem.monetary };
      let client = { ...this.selectedItem.client };
      let title = '';
      if (monetary.clientDetails != null) {
        let accountId = monetary.clientDetails.accountId || "";
        title = accountId + ' ' + client.firstName + ' ' + client.lastName;
      }
      return 'Comment Transaction ' + title;

    },
    async saveComment() {
      let $this = this;
      if (this.selectedItem.monetary.paymentDetails == null) {
        this.selectedItem.monetary.paymentDetails = {};
      }

      this.selectedItem.monetary.paymentDetails.comments = this.comment;
      console.log('Saved Comment for item:');
      await this.updateMonetary({
        monetary: this.selectedItem.monetary,
        id: this.selectedItem.monetary.id
      })
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }), err.response.data.error);
          });
      this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.monetaries') }));
      this.$emit('input', this.monetary);
      this.isCommentModalVisible = false;
      this.selectedItem = {}; // Clear the selected item
      this.comment = ''; // Clear the comment field

    },

    truncateText(text) {
      const maxLength = 40; // Adjust the maximum length as needed
      return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
    },
    getStatusVariant(status) {
      switch (status) {
        case 'DEPOSIT':
          return 'success';
        case 'CARD':
          return 'warning';
        case 'WITHDRAWAL':
          return 'danger';
        default:
          return 'primary';
      }
    },
    async refresh() {
      let $this = this;

      let loadingToken = this.setLoading();
      let sort = { ...$this.sort };
      if (sort.field == null) {
        sort.field = 'lastUpdate';
      }

      // Remember what we're about to fetch so the deep `filters` watcher can
      // skip a redundant fire with identical content (but still react to real
      // changes such as the deposit/withdraw toggle).
      this.lastFiltersKey = JSON.stringify(this.filters);

      // console.log("refresh()", {  pagination: this.pagination, sort})
      const result = await this.getAllMonetary(
          {
            pagination: this.pagination,
            sort,
            filter: this.filters,
          }
      )
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.monetaries') }), $this.getErrorDescription(err));
            $this.setLoaded(loadingToken);
          });
      this.monetaries = result.data['monetaryTransactions'];
      this.clients = result.data['clients'].reduce((map, obj) => (map[obj.id] = obj, map), {});
      this.setMetaDataFromResult(result);
      this.setLoaded(loadingToken);
      this.$emit('total', this.pagination.total);
      this.$emit('sum', (this.monetaries || []).reduce((t, m) => t + (Number(m.amount) || 0), 0));
    },
    createExcelFile() {
      let $this = this;
      if ($this.exportingInProgress) {
        this.showWarningNotification('Exporting data to Excel is already in progress. Please wait...');
        return;
      }
      this.showInfoNotification('Exporting data to Excel...');
      this.exportingInProgress = true;
      let sort = { ...$this.sort };
      if (sort.field == null) {
        sort.field = 'lastUpdate';
      }
      this.exportMonetaryDataExcel({
        pagination: this.pagination,
        sort,
        filter: this.filters,
      }).finally(() => {
        $this.exportingInProgress = false;
      });
    },
  }
};
</script>

<style scoped>
.custom-modal {
  min-width: 500px; /* Adjust the width as needed */
}

.overflow-auto {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
